/**
 * The join between an artwork reference and the origin that serves it.
 *
 * `backend-assets` states artwork as a ROOT-RELATIVE PATH —
 * `/assets-artwork/public/content_item/<ulid>/thumbnail-<ulid>` — on every read:
 * the item list's `cover_artwork_url`, `GET /artworks`, and the editorial post's
 * `thumbnailUrl` / `heroArtworkUrl`. That path is not servable from this host.
 * The console is a static SPA root with a history fallback, so the browser
 * resolves the same string against `admin.<host>`, gets `index.html` back with a
 * `200 text/html`, and the `<img>` decodes nothing — `naturalWidth` stays `0`
 * and every cover in the console renders blank with no error anywhere.
 *
 * The sibling public site closed the same gap with `NEXT_PUBLIC_ASSET_ORIGIN`;
 * `VITE_ASSET_ORIGIN` is that variable on this stack, and this module is its
 * only reader. The join lives in one function rather than at each `<img>`
 * because a caller that forgets it fails silently — which is exactly how the
 * bug survived a full authoring pass.
 */

/**
 * The platform's own artwork origin, trailing slashes stripped.
 *
 * The fallback is the production host rather than an empty string: empty would make
 * `artworkUrl` a no-op that silently reproduces the bug this module exists to
 * close, while a wrong absolute host fails visibly as a cross-origin 404.
 */
export function assetOrigin(): string {
  return (import.meta.env.VITE_ASSET_ORIGIN ?? 'https://storage.shahedni.com').replace(/\/+$/, '');
}

/**
 * Absolute URL for one artwork reference, or `null` when there is no reference.
 *
 * `null` in, `null` out: an absent artwork is the gradient stand-in the callers
 * already branch to, never a broken image element.
 *
 * A value that is ALREADY absolute is returned untouched. The owning service
 * states paths today and nothing here should break if it ever states URLs — a
 * joiner that blindly prefixed would produce `https://storage…/https://…`, and
 * that failure looks identical to the one this module removes.
 *
 * A `blob:` or `data:` reference is also returned untouched: the upload controls
 * preview the file the operator just chose before any URL exists on the wire.
 */
export function artworkUrl(reference: string | null | undefined): string | null {
  if (!reference) return null;
  if (/^(https?:|blob:|data:)/i.test(reference)) return reference;
  return `${assetOrigin()}${reference.startsWith('/') ? reference : `/${reference}`}`;
}
